// src/App.tsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Login from './pages/Login/Login';
import OrdersList from './pages/Orders/OrdersList/OrdersList';
import OrderManagement from './pages/Orders/OrderManagement/OrderManagement';
import CreateOrder from './pages/Orders/CreateOrder/CreateOrder';
import CreateUser from './pages/Users/CreateUser/CreateUser';
import UsersList from './pages/Users/UsersList/UsersList';
import ClientReportGeneration from './pages/Reports/ClientReportGeneration/ClientReportGeneration';
import SiloDeliveries from './pages/Deliveries/SiloDeliveries/SiloDeliveries';
import DeliveryCertificates from './pages/Deliveries/DeliveryCertificates/DeliveryCertificates';
import ProductIdCreation from './pages/Products/ProductIdCreation/ProductIdCreation';
import Layout from './components/Layout/Layout';
import { useAuth } from './context/AuthContext';
import SentryErrorBoundary from './components/SentryErrorBoundary';
import { defaultErrorHandler } from './services/ErrorHandler';
import { captureException } from './services/sentry';
import { ErrorType } from './models/ApiError';

// Configure React Query with Sentry error logging
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (error) => {
        // Convert to ApiError and send to Sentry
        const apiError = defaultErrorHandler.handle(error);
        if (apiError.type !== ErrorType.VALIDATION_ERROR) {
          captureException(error instanceof Error ? error : new Error(String(error)), {
            tags: {
              source: 'react-query',
              mutation: 'true',
            },
          });
        }
      },
    },
  },
});

const AppRoutes: React.FC = () => {
  const { isLoggedIn } = useAuth();

  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        <Route path="/" element={<Layout />}>
          {!isLoggedIn ? (
            <>
              {/* Set the default index route to Login when not logged in */}
              <Route index element={<Login />} />
              <Route path="login" element={<Login />} />
              {/* Any unknown route redirects to /login */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </>
          ) : (
            <>
              {/* When logged in, set index to a protected page */}
              <Route index element={<OrdersList />} />
              <Route path="orders" element={<OrdersList />} />
              <Route path="create-order" element={<CreateOrder />} />
              <Route path="order-management" element={<OrderManagement />} />

              <Route path="create-user" element={<CreateUser />} />
              <Route path="users" element={<UsersList />} />
              <Route path="*" element={<Navigate to="/" replace />} />
              
              <Route path="client-report-generation" element={<ClientReportGeneration />} />
              <Route path="silo-deliveries" element={<SiloDeliveries />} />
              <Route path="delivery-certificates" element={<DeliveryCertificates />} />
              <Route path="product-id-creation" element={<ProductIdCreation />} />
            </>
          )}
        </Route>
      </Routes>
    </QueryClientProvider>
  );
};

const App: React.FC = () => (
  <Router>
    <SentryErrorBoundary>
      <AppRoutes />
    </SentryErrorBoundary>
  </Router>
);

export default App;
